import {
    type CSSProperties,
    useEffect,
    useState,
    useRef
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerPickerType,
    type DateTimePickerDateRange,
    type DateTimeFormatPreset,
    type IDateTimeSheetProps,
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    formatDateTime,
    DateTimeSheet,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";

const NAME = "DateTimeSheet";

const USAGE_CODE = `const sheetRef = useRef<IBottomSheetRef>(null);

const [isOpen, setIsOpen] = useState(false);

const [draft, setDraft] = useState<Date>();

const [date, setDate] = useState<Date>();

<Button
    onPress={() => {
        setDraft(date);

        setIsOpen(true);
    }}
    title="Pick a date"
/>

<DateTimeSheet
    ok={() => {
        if(!draft) {
            return false;
        }

        setDate(draft);
    }}
    cancel={() => sheetRef.current?.close()}
    selectMultipleObject={() => undefined}
    clear={() => setDraft(undefined)}
    dateTimePickerKey="due-date"
    isWorkWithRealtime={false}
    bottomSheetRef={sheetRef}
    onOpenChange={setIsOpen}
    setIsActive={setIsOpen}
    selectObject={setDraft}
    pickerType="date"
    isOpen={isOpen}
    isLoading={false}
    variant="single"
    date={draft}
/>`;

const POPOVER_CODE = `const anchorRef = useRef<View>(null);

<View
    ref={anchorRef}
>
    <Button
        onPress={() => setIsOpen(!isOpen)}
        title="Pick a date"
    />
</View>

<DateTimeSheet
    anchorRef={anchorRef}
/>`;

type DateTimeSheetDemoProps = Partial<Pick<IDateTimeSheetProps, "isWorkWithRealtime" | "isShowCalendarChangerTool" | "multipleSelectDayLimit" | "dateCalendarType" | "isShowTools" | "pickerType" | "minChoice" | "variant" | "minDate">> & {
    initialDateRange?: DateTimePickerDateRange;
    isLoadingOnOpen?: boolean;
    isAnchored?: boolean;
    buttonTitle: string;
    initialDate?: Date;
    sheetKey: string;
};

const TODAY = new Date();

const TODAY_START = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());

const SAMPLE_DATE = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate(), 14, 30);

const DAY_LIMIT = 14;

const LOADING_DELAY = 1500;

const SHEET_PROPS = {
    isWorkWithAnimation: true
};

const FORMAT_PRESETS: Record<DateTimePickerPickerType, DateTimeFormatPreset> = {
    "date-time": "dateTime",
    date: "date",
    time: "time"
};

const DEMO_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
};

const DateTimeSheetDemo = ({
    isWorkWithRealtime = false,
    isLoadingOnOpen = false,
    pickerType = "date",
    isAnchored = false,
    variant = "single",
    initialDateRange,
    buttonTitle,
    initialDate,
    sheetKey,
    ...props
}: DateTimeSheetDemoProps) => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();

    const sheetRef = useRef<IBottomSheetRef>(null);

    const anchorRef = useRef<View>(null);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        date,
        setDate
    ] = useState<Date | undefined>(initialDate);

    const [
        dateRange,
        setDateRange
    ] = useState<DateTimePickerDateRange | undefined>(initialDateRange);

    const [
        draftDate,
        setDraftDate
    ] = useState<Date | undefined>(initialDate);

    const [
        draftDateRange,
        setDraftDateRange
    ] = useState<DateTimePickerDateRange | undefined>(initialDateRange);

    const currentDate = isWorkWithRealtime ? date : draftDate;

    const currentDateRange = isWorkWithRealtime ? dateRange : draftDateRange;

    useEffect(() => {
        if(!isOpen || !isLoadingOnOpen) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoadingOnOpen,
        isOpen
    ]);

    const formatValue = (value: Date) => formatDateTime({
        preset: FORMAT_PRESETS[pickerType],
        locale: activeLocale,
        date: value
    });

    const getReadout = () => {
        if(variant === "range") {
            if(!dateRange?.start || !dateRange.end) {
                return t("dateTimeSheetPage.noSelection");
            }

            return t("dateTimeSheetPage.selectedRange", [
                formatValue(dateRange.start),
                formatValue(dateRange.end)
            ]);
        }

        if(!date) {
            return t("dateTimeSheetPage.noSelection");
        }

        return t("dateTimeSheetPage.selectedDate", [
            formatValue(date)
        ]);
    };

    const handleOpen = () => {
        if(!isWorkWithRealtime) {
            setDraftDateRange(dateRange);

            setDraftDate(date);
        }

        setIsLoading(isLoadingOnOpen);

        setIsOpen(true);
    };

    const handleCancel = () => sheetRef.current?.close();

    const handlePress = () => {
        if(isOpen) {
            handleCancel();

            return;
        }

        handleOpen();
    };

    const handleSelectObject = (selectedDate?: Date) => {
        if(isWorkWithRealtime) {
            setDate(selectedDate);

            return;
        }

        setDraftDate(selectedDate);
    };

    const handleSelectMultipleObject = (selectedRange?: DateTimePickerDateRange) => {
        if(isWorkWithRealtime) {
            setDateRange(selectedRange);

            return;
        }

        setDraftDateRange(selectedRange);
    };

    const handleClear = () => {
        if(isWorkWithRealtime) {
            setDateRange(undefined);

            setDate(undefined);

            return;
        }

        setDraftDateRange(undefined);

        setDraftDate(undefined);
    };

    const handleOk = (): boolean | void => {
        if(isWorkWithRealtime) {
            return;
        }

        if(variant === "range") {
            if(!draftDateRange?.start) {
                NCoreUIKitToast.open({
                    title: localize("start-date-select-is-required")
                });

                return false;
            }

            if(!draftDateRange.end) {
                NCoreUIKitToast.open({
                    title: localize("end-date-select-is-required")
                });

                return false;
            }
        }

        setDateRange(draftDateRange);

        setDate(draftDate);
    };

    return <div
        style={DEMO_STYLE}
    >
        <View
            ref={anchorRef}
        >
            <Button
                onPress={handlePress}
                title={buttonTitle}
                variant="outline"
                type="neutral"
            />
        </View>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {getReadout()}
        </Text>
        <DateTimeSheet
            {...props}
            selectMultipleObject={handleSelectMultipleObject}
            anchorRef={isAnchored ? anchorRef : undefined}
            isWorkWithRealtime={isWorkWithRealtime}
            selectObject={handleSelectObject}
            bottomSheetProps={SHEET_PROPS}
            dateRange={currentDateRange}
            dateTimePickerKey={sheetKey}
            bottomSheetRef={sheetRef}
            setIsActive={setIsOpen}
            pickerType={pickerType}
            isLoading={isLoading}
            cancel={handleCancel}
            clear={handleClear}
            date={currentDate}
            variant={variant}
            isOpen={isOpen}
            ok={handleOk}
        />
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("dateTimeSheetPage.pickerTitle"),
        id: "picker"
    },
    {
        title: t("dateTimeSheetPage.applyTitle"),
        id: "apply"
    },
    {
        title: t("dateTimeSheetPage.viewsTitle"),
        id: "views"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("dateTimeSheetPage.popoverTitle"),
        id: "popover"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DateTimeSheetPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("dateTimeSheetPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dateTimeSheetPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.overviewCaption")}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.openDate")}
                    sheetKey="overview"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("dateTimeSheetPage.overviewNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("dateTimeSheetPage.pickerTitle")}
            id="picker"
        >
            <Paragraph>
                {t("dateTimeSheetPage.pickerText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.pickerCaption", [
                    DAY_LIMIT
                ])}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.openDateTime")}
                    initialDate={SAMPLE_DATE}
                    pickerType="date-time"
                    sheetKey="date-time"
                />
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.openTime")}
                    initialDate={SAMPLE_DATE}
                    pickerType="time"
                    sheetKey="time"
                />
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.openRange")}
                    multipleSelectDayLimit={DAY_LIMIT}
                    minDate={TODAY_START}
                    sheetKey="range"
                    variant="range"
                />
            </Preview>
        </Section>
        <Section
            title={t("dateTimeSheetPage.applyTitle")}
            id="apply"
        >
            <Paragraph>
                {t("dateTimeSheetPage.applyText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.applyCaption")}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.applyOpen")}
                    isWorkWithRealtime={true}
                    sheetKey="instant"
                />
            </Preview>
        </Section>
        <Section
            title={t("dateTimeSheetPage.viewsTitle")}
            id="views"
        >
            <Paragraph>
                {t("dateTimeSheetPage.viewsText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.viewsCaption")}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.viewsOpenMonth")}
                    initialDate={SAMPLE_DATE}
                    dateCalendarType="month"
                    sheetKey="month"
                />
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.viewsOpenDay")}
                    isShowCalendarChangerTool={false}
                    initialDate={SAMPLE_DATE}
                    sheetKey="day"
                />
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.viewsOpenTools")}
                    initialDate={SAMPLE_DATE}
                    isShowTools={false}
                    sheetKey="tools"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("dateTimeSheetPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.statesCaption")}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.statesOpenLoading")}
                    initialDate={SAMPLE_DATE}
                    isLoadingOnOpen={true}
                    sheetKey="loading"
                />
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.statesOpenRequired")}
                    initialDate={SAMPLE_DATE}
                    sheetKey="required"
                    minChoice={1}
                />
            </Preview>
        </Section>
        <Section
            title={t("dateTimeSheetPage.popoverTitle")}
            id="popover"
        >
            <Paragraph>
                {t("dateTimeSheetPage.popoverText")}
            </Paragraph>
            <Preview
                caption={t("dateTimeSheetPage.popoverCaption")}
            >
                <DateTimeSheetDemo
                    buttonTitle={t("dateTimeSheetPage.popoverOpen")}
                    sheetKey="popover"
                    isAnchored={true}
                />
            </Preview>
            <CodeBlock
                code={POPOVER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dateTimeSheetPage.a11ySheet"),
                    t("dateTimeSheetPage.a11yPopover"),
                    t("dateTimeSheetPage.a11yViews"),
                    t("dateTimeSheetPage.a11yCalendar"),
                    t("dateTimeSheetPage.a11yTime"),
                    t("dateTimeSheetPage.a11yLoading"),
                    t("dateTimeSheetPage.a11yFooter"),
                    t("dateTimeSheetPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("dateTimeSheetPage.propsRequired")}
            </Paragraph>
            <Paragraph>
                {t("dateTimeSheetPage.propsValueIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimeSheetPage.propPickerType"),
                        type: "\"date\" | \"time\" | \"date-time\"",
                        name: "pickerType"
                    },
                    {
                        description: t("dateTimeSheetPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        name: "variant"
                    },
                    {
                        description: t("dateTimeSheetPage.propDate"),
                        type: "Date",
                        name: "date"
                    },
                    {
                        description: t("dateTimeSheetPage.propDateRange"),
                        type: "{ start?: Date; end?: Date }",
                        name: "dateRange"
                    },
                    {
                        description: t("dateTimeSheetPage.propSelectObject"),
                        type: "(date?: Date) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("dateTimeSheetPage.propSelectMultipleObject"),
                        type: "(dateRange?: DateTimePickerDateRange) => void",
                        name: "selectMultipleObject"
                    },
                    {
                        description: t("dateTimeSheetPage.propSelectDateRule"),
                        type: "(dateRule?: RRule) => void",
                        name: "selectDateRule"
                    },
                    {
                        description: t("dateTimeSheetPage.propDateRule"),
                        name: "dateRule",
                        type: "RRule"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsWorkWithRealtime"),
                        name: "isWorkWithRealtime",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propOk"),
                        type: "() => boolean | void",
                        name: "ok"
                    },
                    {
                        description: t("dateTimeSheetPage.propCancel"),
                        type: "() => void",
                        name: "cancel"
                    },
                    {
                        description: t("dateTimeSheetPage.propClear"),
                        type: "() => void",
                        name: "clear"
                    },
                    {
                        description: t("dateTimeSheetPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dateTimeSheetPage.propDescription"),
                        name: "description",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimeSheetPage.propsStateIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimeSheetPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("dateTimeSheetPage.propSetIsActive"),
                        type: "Dispatch<SetStateAction<boolean>>",
                        name: "setIsActive"
                    },
                    {
                        description: t("dateTimeSheetPage.propBottomSheetRef"),
                        type: "Ref<IBottomSheetRef> | null",
                        name: "bottomSheetRef"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsLoading"),
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propDateTimePickerKey"),
                        name: "dateTimePickerKey",
                        type: "string"
                    },
                    {
                        description: t("dateTimeSheetPage.propAnchorRef"),
                        type: "RefObject<View | null>",
                        name: "anchorRef"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimeSheetPage.propsLimitsIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimeSheetPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("dateTimeSheetPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("dateTimeSheetPage.propMultipleSelectDayLimit"),
                        name: "multipleSelectDayLimit",
                        type: "number"
                    },
                    {
                        description: t("dateTimeSheetPage.propMultipleSelectMinimumRequiredDayCount"),
                        name: "multipleSelectMinimumRequiredDayCount",
                        type: "number"
                    },
                    {
                        description: t("dateTimeSheetPage.propMultipleSelectMinimumRequireMS"),
                        name: "multipleSelectMinimumRequireMS",
                        type: "number"
                    },
                    {
                        description: t("dateTimeSheetPage.propMultipleSelectMaximumMS"),
                        name: "multipleSelectMaximumMS",
                        type: "number"
                    },
                    {
                        description: t("dateTimeSheetPage.propMinChoice"),
                        name: "minChoice",
                        type: "number"
                    },
                    {
                        description: t("dateTimeSheetPage.propMaxChoice"),
                        name: "maxChoice",
                        type: "number"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimeSheetPage.propsLookIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimeSheetPage.propDateCalendarType"),
                        type: "\"day\" | \"month\" | \"year\"",
                        name: "dateCalendarType"
                    },
                    {
                        description: t("dateTimeSheetPage.propOnDateCalendarTypeChange"),
                        type: "(calendarType: \"day\" | \"month\" | \"year\") => void",
                        name: "onDateCalendarTypeChange"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsShowCalendarChangerTool"),
                        name: "isShowCalendarChangerTool",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsShowTools"),
                        defaultValue: "true",
                        name: "isShowTools",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsShowWeekNumber"),
                        name: "isShowWeekNumber",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propMonthCount"),
                        name: "monthCount",
                        defaultValue: "1",
                        type: "1 | 2"
                    },
                    {
                        description: t("dateTimeSheetPage.propCaptionLayout"),
                        type: "\"label\" | \"dropdown\"",
                        defaultValue: "\"label\"",
                        name: "captionLayout"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsShowTodayIndicator"),
                        name: "isShowTodayIndicator",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsWorkWith24HoursFormat"),
                        name: "isWorkWith24HoursFormat",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsWorkWithSeconds"),
                        name: "isWorkWithSeconds",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propIsTimeSelectWorksWithCenter"),
                        name: "isTimeSelectWorksWithCenterWhenTimePicker",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propDayOfWeekLength"),
                        type: "\"short\" | \"very-short\" | \"long\"",
                        defaultValue: "\"short\"",
                        name: "dayOfWeekLength"
                    },
                    {
                        description: t("dateTimeSheetPage.propMonthOfYearLengthType"),
                        name: "monthOfYearLengthType",
                        type: "\"short\" | \"long\"",
                        defaultValue: "\"short\""
                    },
                    {
                        description: t("dateTimeSheetPage.propLocaleBasedFirstDayOfWeek"),
                        name: "localeBasedFirstDayOfWeek",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimeSheetPage.propLoadingIcon"),
                        name: "LoadingIconComponentProp",
                        type: "NCoreUIKitIcon"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimeSheetPage.propsSheetIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimeSheetPage.propBottomSheetProps"),
                        type: "Omit<IBottomSheetProps, ...>",
                        name: "bottomSheetProps"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dateTimeSheetPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dateTimeSheetPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dateTimeSheetPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DateTimeSheetPage;
